import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Clock, AlertTriangle,
  ArrowLeft, ArrowRight, Eye, Save,
  Send, RefreshCw, Timer,
  FileText, Info, AlertCircle, Shield, EyeOff, LogOut, Trash2
} from 'lucide-react';
import toast from 'react-hot-toast';
import apiClient from '@/services/api';
import { API_ENDPOINTS } from '@/config/api.config';
import { Leaderboardservice, EarnedBadge } from '@/services/leaderboard.service';

interface QuestionOption {
  id: string;
  text: string;
  is_correct: boolean;
  explanation?: string;
}

interface Question {
  _id: string;
  question_text: string;
  question_type: string; // Allow any question type from API
  options?: QuestionOption[];
  correct_answer: string | string[];
  points: number;
  explanation?: string;
  userAnswer?: string | string[];
  isAnswered: boolean;
}

interface CertificationExamData {
  _id: string;
  certification_id: string;
  title: string;
  questions: any[];
  total_points: number;
  passing_score: number;
  time_limit_minutes?: number;
  shuffle_questions: boolean;
  shuffle_options: boolean;
  allow_retry?: boolean;
  max_attempts?: number;
}

interface ExamState {
  id: string;
  title: string;
  totalQuestions: number;
  currentQuestion: number;
  timeRemaining: number;
  totalTime: number;
  timeLimitMinutes: number;
  questions: Question[];
  isSubmitted: boolean;
  autoSaveEnabled: boolean;
  lastSaved: string;
  violations: {
    type: string;
    timestamp: string;
    count: number;
  }[];
  violationCount: number;
}

/**
 * LearnerDoExam Component
 *
 * State Management Strategy:
 * - API is the authoritative source for saved answers
 * - On initial load: Fetch attempt details from API once and bind saved answers to UI
 * - On save: Submit answer to API, then fetch latest attempt details to refresh UI
 * - localStorage is used as backup for temporary state (not for saved answers)
 *
 * API Call Flow:
 * 1. Page loads → GET /latest-attempts (once on initial load)
 * 2. User clicks "Save Changes" → POST /submit-answer → GET /latest-attempts (after each save)
 *
 * Answer Flow:
 * 1. User selects/enters answer → Stored in examState.questions[].userAnswer
 * 2. User clicks "Save Changes" → Submit to API with proper format
 * 3. API saves answer → Fetch latest attempt details
 * 4. Bind saved answers from API → Update UI to show saved state
 */

// Server deadline (started_at + limit) in ms, or null when started_at is unknown
const getServerDeadlineMs = (startedAt?: string, limitMinutes?: number): number | null => {
  if (!startedAt || !limitMinutes) return null;
  const startedMs = new Date(startedAt).getTime();
  return Number.isNaN(startedMs) ? null : startedMs + limitMinutes * 60 * 1000;
};

const getServerRemainingSeconds = (startedAt?: string, limitMinutes?: number): number | null => {
  const deadlineMs = getServerDeadlineMs(startedAt, limitMinutes);
  return deadlineMs === null ? null : Math.max(0, Math.floor((deadlineMs - Date.now()) / 1000));
};

// Seconds left: the server's remaining_seconds when sent (older backends: started_at + limit), never above the limit
const resolveRemainingSeconds = (attempt: any, limitMinutes: number): number | null => {
  const remaining = typeof attempt?.remaining_seconds === 'number'
    ? attempt.remaining_seconds
    : getServerRemainingSeconds(attempt?.started_at, limitMinutes);
  return remaining === null ? null : Math.min(remaining, limitMinutes * 60);
};

// Countdown end on the page's own stopwatch, so a wrong system clock can't change it
const getCountdownEndMs = (remainingSeconds: number | null): number | null =>
  remainingSeconds === null ? null : performance.now() + remainingSeconds * 1000;

export default function LearnerDoExam() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [certificationData, setCertificationData] = useState<CertificationExamData | null>(null);
  const [loading, setLoading] = useState(true);
  const [examState, setExamState] = useState<ExamState | null>(null);
  const [showNavigationPanel, setShowNavigationPanel] = useState(true);
  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false);
  const [showTimeWarning, setShowTimeWarning] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'saving' | 'saved' | 'error'>('saved');
  const [, setIsTabVisible] = useState(true);
  const [showViolationWarning, setShowViolationWarning] = useState(false);
  const [currentViolationType, setCurrentViolationType] = useState('');
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAbortingPopup, setShowAbortingPopup] = useState(false);
  const [metricsLoaded, setMetricsLoaded] = useState(false);

  const examContainerRef = useRef<HTMLDivElement>(null);
  const _autoSaveInterval = useRef<NodeJS.Timeout>();
  const metricsUpdateInterval = useRef<NodeJS.Timeout>(); // For 5-second metrics update
  const lastViolationTime = useRef<{ [key: string]: number }>({});
  const lastGlobalViolationTime = useRef<number>(0); // Global debounce for any violation type
  const isHandlingFullscreen = useRef(false);
  const tabSwitchTimeout = useRef<NodeJS.Timeout>();
  const questionStartTime = useRef<number>(Date.now());
  const hasLoadedAttemptDetails = useRef(false); // Track if we've loaded attempt details on initial load
  const isAbandoning = useRef(false); // Track if abandon is already in progress
  const autoSubmitTriggered = useRef(false); // Auto-submit fires once when time runs out
  const countdownEndMs = useRef<number | null>(null); // performance.now() value at which time runs out
  const submitExamRef = useRef<() => Promise<void>>(async () => {});
  const isSubmittingOrExiting = useRef(false); // Track if exam is being submitted/exited - stop violation monitoring
  const examStartTime = useRef<number>(Date.now()); // Track when exam started for time calculation
  const initialTimeSpentSeconds = useRef<number>(0); // Track initial time spent for continuing exams
  const currentViolationCount = useRef<number>(0); // Track current violation count for metrics
  const savedAnswersRef = useRef<Map<string, { answer: string | string[], isAnswered: boolean }>>(new Map()); // Track saved answers from backend

  // Get storage key for this exam (certification-specific)
  const getStorageKey = () => {
    const key = `exam_state_${examId}`;
    console.log('📝 getStorageKey:', key);
    return key;
  };
  const getTimerStorageKey = () => {
    const key = `exam_timer_${examId}`;
    console.log('📝 getTimerStorageKey:', key);
    return key;
  };
  const getViolationsStorageKey = () => {
    const key = `exam_violations_${examId}`;
    console.log('📝 getViolationsStorageKey:', key);
    return key;
  };

  // Clear all localStorage keys for this exam
  const clearAllExamStorage = useCallback(() => {
    console.log('🧹 clearAllExamStorage called for examId:', examId);

    const stateKey = `exam_state_${examId}`;
    const timerKey = `exam_timer_${examId}`;
    const violationsKey = `exam_violations_${examId}`;

    console.log('   - Removing state key:', stateKey);
    console.log('   - Removing timer key:', timerKey);
    console.log('   - Removing violations key:', violationsKey);

    // Remove all keys
    localStorage.removeItem(stateKey);
    localStorage.removeItem(timerKey);
    localStorage.removeItem(violationsKey);

    // Also scan and remove any keys that match the pattern (in case of any variations)
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.includes(`exam_state_${examId}`) ||
        key.includes(`exam_timer_${examId}`) ||
        key.includes(`exam_violations_${examId}`)
      )) {
        keysToRemove.push(key);
      }
    }

    // Remove any matching keys found
    keysToRemove.forEach(key => {
      console.log('   - Removing additional matching key:', key);
      localStorage.removeItem(key);
    });

    // Verify removal
    const stillExists = {
      state: localStorage.getItem(stateKey),
      timer: localStorage.getItem(timerKey),
      violations: localStorage.getItem(violationsKey)
    };

    console.log('✅ Verification after removal:', {
      stateExists: stillExists.state !== null,
      timerExists: stillExists.timer !== null,
      violationsExists: stillExists.violations !== null,
      additionalKeysRemoved: keysToRemove.length
    });

    if (stillExists.state || stillExists.timer || stillExists.violations) {
      console.error('⚠️ Some keys still exist in localStorage!', stillExists);
    } else {
      console.log('✅ All localStorage keys cleared successfully');
    }
  }, [examId]);

  // Bind saved answers from API to exam state
  const bindSavedAnswers = useCallback((questionAttempts: any[]) => {
    console.log('Binding saved answers from API');

    setExamState(prev => {
      if (!prev) return prev;

      const updatedQuestions = prev.questions.map(question => {
        // Find the saved attempt for this question
        const savedAttempt = questionAttempts.find(qa => qa.question_id === question._id);

        if (savedAttempt && savedAttempt.answer_submitted) {
          console.log(`Found saved answer for question ${question._id}:`, savedAttempt.answer_submitted);

          let userAnswer: string | string[] | undefined;

          // Convert answer_submitted back to display format
          if (question.question_type === 'SINGLE_CHOICE' || question.question_type === 'TRUE_FALSE') {
            // answer_submitted is an option ID, convert to option text
            const option = question.options?.find(opt => opt.id === savedAttempt.answer_submitted);
            userAnswer = option ? option.text : savedAttempt.answer_submitted;
          } else if (question.question_type === 'MULTIPLE_CHOICE') {
            // answer_submitted is array of option IDs, convert to array of option texts
            if (Array.isArray(savedAttempt.answer_submitted)) {
              userAnswer = savedAttempt.answer_submitted
                .map((answerId: string) => {
                  const option = question.options?.find(opt => opt.id === answerId);
                  return option ? option.text : answerId;
                })
                .filter((text: string) => text !== undefined);
            } else {
              userAnswer = [];
            }
          } else {
            // TEXT_ANSWER or other types - use as is
            userAnswer = savedAttempt.answer_submitted;
          }

          // Store in ref for quick access during navigation
          savedAnswersRef.current.set(question._id, {
            answer: userAnswer ?? '',
            isAnswered: true
          });

          return {
            ...question,
            userAnswer,
            isAnswered: true
          };
        }

        // If no saved answer but was previously marked as answered, keep the saved state from ref
        if (savedAnswersRef.current.has(question._id)) {
          const saved = savedAnswersRef.current.get(question._id)!;
          return {
            ...question,
            userAnswer: saved.answer,
            isAnswered: saved.isAnswered
          };
        }

        // If no saved answer, ensure unsaved changes are cleared
        return {
          ...question,
          userAnswer: undefined,
          isAnswered: false
        };
      });

      const newState = {
        ...prev,
        questions: updatedQuestions
      };

      console.log('Updated examState with saved answers:', newState);
      return newState;
    });
  }, []);

  // Fetch latest attempt details from API
  const fetchLatestAttemptDetails = useCallback(async () => {
    if (!examId) {
      console.warn('Cannot fetch attempt details: missing examId');
      return;
    }

    try {
      console.log('📡 Fetching latest attempt details from API...');
      const response = await apiClient.get(API_ENDPOINTS.CERTIFICATIONS.LATEST_ATTEMPTS(examId));

      if (response.data.success && response.data.data) {
        const attemptDetails = response.data.data;
        console.log('✅ Received attempt details:', attemptDetails);
        console.log(`   - Attempt ID: ${attemptDetails.id || attemptDetails._id}`);
        console.log(`   - Status: ${attemptDetails.status}`);
        console.log(`   - Total saved answers: ${attemptDetails.question_attempts?.length || 0}`);
        console.log(`   - overall_time_spent_seconds: ${attemptDetails.overall_time_spent_seconds}`);
        console.log(`   - violation_count: ${attemptDetails.violation_count}`);

        // Check if attempt is already ABANDONED - redirect to certifications
        if (attemptDetails.status === 'ABANDONED') {
          console.log('🚫 Attempt is ABANDONED - redirecting to certifications');
          clearAllExamStorage();
          toast.error('This exam was abandoned due to violations.');
          navigate('/learn/certifications');
          return;
        }

        // Check if attempt is already SUBMITTED - redirect to certifications
        if (attemptDetails.status === 'SUBMITTED' || attemptDetails.status === 'COMPLETED') {
          console.log('✅ Attempt is already SUBMITTED/COMPLETED - redirecting to certifications');
          clearAllExamStorage();
          toast.success('This exam has already been submitted.');
          navigate('/learn/certifications');
          return;
        }

        // Check if violation count already exceeds limit (> 3)
        const apiViolationCount = attemptDetails.violation_count || 0;
        if (apiViolationCount > 3) {
          console.log('🚫 Violation count exceeds limit on page load:', apiViolationCount);
          clearAllExamStorage();
          toast.error('Exam abandoned due to exceeding violation limit.');
          navigate('/learn/certifications');
          return;
        }

        // Set attempt ID if not already set
        const currentAttemptId = attemptDetails.id || attemptDetails._id;
        if (currentAttemptId && !attemptId) {
          console.log('🔑 Setting attemptId from LATEST_ATTEMPTS:', currentAttemptId);
          setAttemptId(currentAttemptId);
        }

        // Update timer and violations from API (authoritative source)
        const timeSpentSeconds = attemptDetails.overall_time_spent_seconds || 0;

        setExamState(prev => {
          if (!prev) return prev;

          // Remaining time comes from the server; falls back to time spent
          const totalTimeLimit = prev.timeLimitMinutes * 60;
          const serverRemaining = resolveRemainingSeconds(attemptDetails, prev.timeLimitMinutes);
          countdownEndMs.current = getCountdownEndMs(serverRemaining);
          const calculatedRemainingTime = serverRemaining ?? Math.max(0, totalTimeLimit - timeSpentSeconds);

          console.log('⏱️ Updating timer from API:', {
            totalTimeLimit,
            timeSpentSeconds,
            calculatedRemainingTime,
            previousTimeRemaining: prev.timeRemaining,
            calculation: `${totalTimeLimit} - ${timeSpentSeconds} = ${calculatedRemainingTime}`
          });

          console.log('⚠️ Updating violations from API:', {
            apiViolationCount,
            previousViolationCount: prev.violationCount
          });

          // Update refs for metrics tracking
          initialTimeSpentSeconds.current = timeSpentSeconds;
          examStartTime.current = Date.now(); // Reset exam start time for this session
          currentViolationCount.current = apiViolationCount;

          return {
            ...prev,
            timeRemaining: calculatedRemainingTime,
            violationCount: apiViolationCount
          };
        });

        // Mark metrics as loaded
        setMetricsLoaded(true);

        // Show violation warning only on the very first load after a page refresh (not on subsequent API calls)
        if (!hasLoadedAttemptDetails.current) {
          const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
          const isRefresh = navigationEntries.length > 0 && navigationEntries[0].type === 'reload';

          if (isRefresh && apiViolationCount > 0) {
            console.log('🔄 Page refresh detected with violations:', apiViolationCount);
            setCurrentViolationType('page_refresh');
            setShowViolationWarning(true);
          }
        }

        // Bind the saved answers from API
        if (attemptDetails.question_attempts && attemptDetails.question_attempts.length > 0) {
          console.log('🔄 Binding saved answers to UI...');
          bindSavedAnswers(attemptDetails.question_attempts);
        } else {
          console.log('ℹ️  No saved answers to bind');
        }
      } else {
        console.warn('⚠️  Failed to fetch attempt details: Invalid response');
        setMetricsLoaded(true); // Still mark as loaded to avoid blocking
      }
    } catch (error) {
      console.error('❌ Error fetching latest attempt details:', error);
      setMetricsLoaded(true); // Still mark as loaded to avoid blocking
    }
  }, [examId, attemptId, bindSavedAnswers, clearAllExamStorage, navigate]);

  // Load exam state from localStorage
  const loadFromStorage = useCallback(() => {
    try {
      const saved = localStorage.getItem(getStorageKey());
      if (saved) {
        const parsed = JSON.parse(saved);
        // Check if saved state is from same exam
        if (parsed.id === examId) {
          return parsed;
        }
      }
    } catch (error) {
      console.error('Error loading from storage:', error);
    }
    return null;
  }, [examId]);

  // Load time remaining from localStorage
  const loadTimeFromStorage = useCallback(() => {
    try {
      const savedTime = localStorage.getItem(getTimerStorageKey());
      if (savedTime) {
        const parsed = JSON.parse(savedTime);
        // Check if saved within last 24 hours (prevent old data)
        const savedAt = new Date(parsed.savedAt).getTime();
        const now = Date.now();
        const hoursDiff = (now - savedAt) / (1000 * 60 * 60);

        if (hoursDiff < 24) {
          return parsed.timeRemaining;
        }
      }
    } catch (error) {
      console.error('Error loading time from storage:', error);
    }
    return null;
  }, [examId]);

  // Save time remaining to localStorage
  const saveTimeToStorage = useCallback((timeRemaining: number) => {
    try {
      localStorage.setItem(getTimerStorageKey(), JSON.stringify({
        timeRemaining,
        savedAt: new Date().toISOString()
      }));
    } catch (error) {
      console.error('Error saving time to storage:', error);
    }
  }, [examId]);

  // Load violations from localStorage (certification-specific)
  const loadViolationsFromStorage = useCallback(() => {
    try {
      const savedViolations = localStorage.getItem(getViolationsStorageKey());
      if (savedViolations) {
        return JSON.parse(savedViolations);
      }
    } catch (error) {
      console.error('Error loading violations from storage:', error);
    }
    return { violations: [], violationCount: 0 };
  }, [examId]);

  // Save violations to localStorage (certification-specific)
  const saveViolationsToStorage = useCallback((violations: any[], violationCount: number) => {
    try {
      localStorage.setItem(getViolationsStorageKey(), JSON.stringify({
        violations,
        violationCount,
        savedAt: new Date().toISOString()
      }));
    } catch (error) {
      console.error('Error saving violations to storage:', error);
    }
  }, [examId]);

  // Save exam state to localStorage
  const saveToStorage = useCallback((state: ExamState) => {
    try {
      localStorage.setItem(getStorageKey(), JSON.stringify(state));
    } catch (error) {
      console.error('Error saving to storage:', error);
    }
  }, [examId]);

  // Fetch certification exam data from API
  const loadCertificationExam = useCallback(async () => {
    if (!examId) return;

    try {
      setLoading(true);

      // Check if we're continuing an in-progress exam
      const attemptDetails = location.state?.attemptDetails;
      const isContinuing = location.state?.isContinuing;

      // Check if there's saved progress first
      const savedState = loadFromStorage();

      const response = await apiClient.get(API_ENDPOINTS.CERTIFICATIONS.GET_DETAILS(examId));

      if (response.data.success) {
        const data = response.data.data;
        setCertificationData(data);

        if (savedState && !savedState.isSubmitted) {
          // Resume from saved state
          console.log('Resuming exam from saved state');

          // Load saved time from localStorage
          const savedTime = loadTimeFromStorage();
          if (savedTime !== null) {
            savedState.timeRemaining = savedTime;
            console.log('⏱️ Restored time remaining:', savedTime, 'seconds');
          }

          // Ensure timeLimitMinutes is set (for backward compatibility)
          if (!savedState.timeLimitMinutes) {
            savedState.timeLimitMinutes = data.time_limit_minutes || 60;
          }

          // Load saved violations from localStorage (certification-specific)
          const savedViolations = loadViolationsFromStorage();
          savedState.violations = savedViolations.violations;
          savedState.violationCount = savedViolations.violationCount;
          console.log('⚠️ Restored violations:', savedViolations.violationCount);

          setExamState(savedState);
          toast.success('Exam resumed from where you left off');
        } else {
          // Start fresh exam
          // Transform API data to exam state
          // Opened without navigation state: recover the in-progress attempt to know its served questions
          let attemptInfo = attemptDetails;
          if (!attemptInfo && data.question_selection?.enabled) {
            try {
              const latest = await apiClient.get(API_ENDPOINTS.CERTIFICATIONS.LATEST_ATTEMPTS(examId));
              if (latest.data.success && latest.data.data?.status === 'IN_PROGRESS') {
                attemptInfo = latest.data.data;
              }
            } catch (err) {
              console.error('Failed to load latest attempt for question selection:', err);
            }
          }

          // With question selection, only the attempt's served questions are shown, in server order
          const servedOrder: string[] = attemptInfo?.question_selection_applied
            ? (attemptInfo.question_order || [])
            : [];
          const sourceQuestions: any[] = servedOrder.length > 0
            ? servedOrder
                .map((id: string) => data.questions.find((q: any) => (q.id || q._id) === id))
                .filter(Boolean)
            : data.questions;

          const transformedQuestions: Question[] = sourceQuestions.map((q: any, index: number) => {
            const questionId = q.id || q._id;
            console.log(`Question ${index + 1} ID:`, questionId);

            return {
              _id: questionId, // Use 'id' field from API, fallback to '_id' if exists
              question_text: q.question_text || '',
              question_type: q.question_type || 'multiple_choice',
              options: q.options || [],
              correct_answer: q.correct_answer,
              points: q.points || 1,
              explanation: q.explanation,
              userAnswer: undefined,
              isAnswered: false
            };
          });

          // Shuffle questions if required (served questions are already shuffled by the server)
          const finalQuestions = data.shuffle_questions && servedOrder.length === 0
            ? shuffleArray(transformedQuestions)
            : transformedQuestions;

          // Shuffle options if required
          if (data.shuffle_options) {
            finalQuestions.forEach(q => {
              if (q.options && q.options.length > 0) {
                q.options = shuffleArray([...q.options]);
              }
            });
          }

          // Calculate time remaining based on whether we're continuing
          let timeRemaining: number;
          let initialTimeSpent = 0;

          const serverRemaining = resolveRemainingSeconds(attemptInfo, data.time_limit_minutes || 60);
          countdownEndMs.current = getCountdownEndMs(serverRemaining);

          if (serverRemaining !== null) {
            // Server deadline is authoritative, so the timer matches what the backend accepts
            timeRemaining = serverRemaining;
            initialTimeSpentSeconds.current = attemptInfo?.overall_time_spent_seconds || 0;
          } else if (isContinuing && attemptDetails) {
            // FOR IN-PROGRESS EXAMS: Get time from API and calculate remaining
            const timeSpentSeconds = attemptDetails.overall_time_spent_seconds || attemptDetails.time_spent_seconds || 0;
            const totalTimeLimit = (data.time_limit_minutes || 60) * 60;
            timeRemaining = Math.max(0, totalTimeLimit - timeSpentSeconds);
            initialTimeSpent = timeSpentSeconds;

            console.log('⏱️ CONTINUING IN-PROGRESS EXAM:', {
              overall_time_spent_seconds: attemptDetails.overall_time_spent_seconds,
              timeSpentSeconds,
              totalTimeLimit,
              calculatedRemainingTime: timeRemaining,
              calculation: `${totalTimeLimit} - ${timeSpentSeconds} = ${timeRemaining}`
            });

            // Store initial time spent for metrics
            initialTimeSpentSeconds.current = initialTimeSpent;
          } else {
            // FOR NEW EXAMS: Try to load saved time (page refresh) or use default
            const savedTime = loadTimeFromStorage();
            timeRemaining = savedTime !== null ? savedTime : (data.time_limit_minutes || 60) * 60;
            console.log('🆕 NEW EXAM - Time:', timeRemaining, 'seconds');
          }

          // Try to load saved violations (certification-specific)
          const savedViolations = loadViolationsFromStorage();

          const examState: ExamState = {
            id: data._id,
            title: data.title,
            totalQuestions: finalQuestions.length,
            currentQuestion: 1,
            timeRemaining: timeRemaining, // Use calculated remaining time
            totalTime: (data.time_limit_minutes || 60) * 60,
            timeLimitMinutes: data.time_limit_minutes || 60,
            questions: finalQuestions,
            isSubmitted: false,
            autoSaveEnabled: true,
            lastSaved: new Date().toISOString(),
            violations: savedViolations.violations,
            violationCount: savedViolations.violationCount
          };

          console.log('🆕 Starting exam with:', {
            timeRemaining,
            totalTime: examState.totalTime,
            isContinuing,
            initialTimeSpent,
            violations: savedViolations.violationCount
          });

          setExamState(examState);
          saveToStorage(examState);

          // Save time to localStorage immediately for in-progress exams
          if (isContinuing) {
            saveTimeToStorage(timeRemaining);
            console.log('💾 Saved adjusted time to localStorage:', timeRemaining);
          }
        }
      }
    } catch (error) {
      console.error('Error loading certification exam:', error);
      toast.error('Failed to load exam');
      navigate('/learn/certifications');
    } finally {
      setLoading(false);
    }
  }, [examId, navigate, loadFromStorage, saveToStorage, loadTimeFromStorage, saveTimeToStorage, loadViolationsFromStorage, location.state]);

  useEffect(() => {
    loadCertificationExam();
  }, [loadCertificationExam]);

  // Suppress browser notifications during the exam (blocks Teams, Slack, etc. web notifications)
  useEffect(() => {
    const OriginalNotification = window.Notification;

    // Override the Notification constructor to silently block all notifications
    const BlockedNotification = function (title: string, _options?: NotificationOptions) {
      console.log('🔇 Notification blocked during exam:', title);
      // Return a dummy object that won't show anything
      return { close: () => {} } as unknown as Notification;
    } as unknown as typeof Notification;

    // Copy static properties
    Object.defineProperty(BlockedNotification, 'permission', {
      get: () => OriginalNotification.permission
    });
    BlockedNotification.requestPermission = OriginalNotification.requestPermission.bind(OriginalNotification);

    window.Notification = BlockedNotification;

    return () => {
      // Restore original Notification on unmount
      window.Notification = OriginalNotification;
    };
  }, []);

  // Get attempt ID from navigation state
  useEffect(() => {
    const attemptDetails = location.state?.attemptDetails;

    if (attemptDetails && attemptDetails.id) {
      setAttemptId(attemptDetails.id);
      console.log('✅ Exam attempt ID set:', attemptDetails.id);
    }
  }, [location.state]);

  // Fetch latest attempt details from API on page load/refresh
  useEffect(() => {
    console.log('Page refresh check. Conditions:', {
      examId: !!examId,
      attemptId: !!attemptId,
      examState: !!examState,
      loading,
      hasLoaded: hasLoadedAttemptDetails.current
    });

    // Trigger API call once when:
    // 1. Page loads/refreshes (examState becomes available after loading completes)
    // 2. examId and examState are ready (attemptId will be fetched from API if missing)
    // 3. Haven't already loaded attempt details
    if (examId && examState && !loading && !hasLoadedAttemptDetails.current) {
      console.log('🎯 Page load/refresh detected: Fetching latest attempt details from API');
      console.log('   - Will get attemptId from LATEST_ATTEMPTS if not already set');
      hasLoadedAttemptDetails.current = true; // Mark as loaded to prevent duplicate calls
      fetchLatestAttemptDetails();
    } else if (hasLoadedAttemptDetails.current) {
      console.log('⏭️  Skipping API call - already loaded attempt details');
    }
  }, [examId, examState, loading, fetchLatestAttemptDetails]);

  // Auto-save examState to localStorage as backup (API is the authoritative source)
  useEffect(() => {
    if (examState && !examState.isSubmitted) {
      saveToStorage(examState);
    }
  }, [examState, saveToStorage]);

  // Fisher-Yates shuffle algorithm
  const shuffleArray = <T,>(array: T[]): T[] => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  // Lock keyboard function - locks Escape and other keys
  const lockKeyboard = async () => {
    if ('keyboard' in navigator && (navigator as any).keyboard?.lock) {
      try {
        await (navigator as any).keyboard.lock([
          'Escape', 'Tab', 'AltLeft', 'AltRight',
          'ControlLeft', 'ControlRight', 'MetaLeft', 'MetaRight',
          'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12'
        ]);
        console.log('✅ Keyboard locked - Escape and other keys are blocked');
      } catch (lockError) {
        console.log('Keyboard lock not available:', lockError);
      }
    }
  };

  // Auto-enter fullscreen when exam loads. Keyed on a boolean, not examState, so the
  // per-second timer update doesn't retry requestFullscreen every second.
  const isExamLoaded = !!examState;
  useEffect(() => {
    const tryEnterFullscreen = async () => {
      if (isExamLoaded && !loading && !isFullscreen && !document.fullscreenElement) {
        try {
          if (examContainerRef.current) {
            await examContainerRef.current.requestFullscreen();
            setIsFullscreen(true);
            // Lock keyboard immediately after entering fullscreen
            await lockKeyboard();
          }
        } catch (error) {
          // Fullscreen request failed (likely due to no user gesture on page refresh)
          // User's click on exam will re-enter fullscreen
          console.log('Auto-fullscreen failed, will enter on click:', error);
        }
      }
    };

    tryEnterFullscreen();
  }, [isExamLoaded, loading, isFullscreen]);

  // Fullscreen management - called by user interaction (click on container)
  const enterFullscreen = async () => {
    try {
      if (examContainerRef.current && !document.fullscreenElement) {
        await examContainerRef.current.requestFullscreen();
        setIsFullscreen(true);
        // Lock keyboard immediately after entering fullscreen
        await lockKeyboard();
      }
    } catch (error) {
      console.error('Fullscreen error:', error);
    }
  };

  // Unlock keyboard when component unmounts or exam ends
  const unlockKeyboard = () => {
    if ('keyboard' in navigator && (navigator as any).keyboard?.unlock) {
      (navigator as any).keyboard.unlock();
    }
  };

  // Handle click on exam container to re-enter fullscreen
  const handleExamContainerClick = useCallback(() => {
    if (examState && !examState.isSubmitted && !isFullscreen && !document.fullscreenElement) {
      enterFullscreen();
    }
  }, [examState, isFullscreen]);

  // Monitor fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!document.fullscreenElement;
      setIsFullscreen(isCurrentlyFullscreen);

      // Don't handle violations if exam is being submitted/exited
      if (isSubmittingOrExiting.current) {
        console.log('🚫 Fullscreen change ignored - exam is being submitted/exited');
        return;
      }

      if (!isCurrentlyFullscreen && examState && !examState.isSubmitted && !isHandlingFullscreen.current) {
        isHandlingFullscreen.current = true;
        handleViolation('fullscreen_exit');

        // Don't show popup here - user's next click will re-enter fullscreen
        // Reset the flag after a short delay
        setTimeout(() => {
          isHandlingFullscreen.current = false;
        }, 1000);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [examState]);

  // Tab visibility detection
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Don't track visibility if exam is being submitted/exited
      if (isSubmittingOrExiting.current) {
        console.log('🚫 Tab visibility change ignored - exam is being submitted/exited');
        return;
      }

      const isVisible = !document.hidden;
      setIsTabVisible(isVisible);

      if (!isVisible && examState && !examState.isSubmitted) {
        // Clear any existing timeout
        if (tabSwitchTimeout.current) {
          clearTimeout(tabSwitchTimeout.current);
        }
        // Only record violation after tab has been hidden for 500ms
        // This prevents accidental quick switches from being recorded
        tabSwitchTimeout.current = setTimeout(() => {
          handleViolation('tab_switch');
        }, 500);
      } else {
        // Clear timeout if user returns before 500ms
        if (tabSwitchTimeout.current) {
          clearTimeout(tabSwitchTimeout.current);
        }
      }
    };

    const handleBlur = () => {
      // Don't track blur if exam is being submitted/exited
      if (isSubmittingOrExiting.current) {
        console.log('🚫 Window blur ignored - exam is being submitted/exited');
        return;
      }

      if (examState && !examState.isSubmitted) {
        handleViolation('window_blur');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      if (tabSwitchTimeout.current) {
        clearTimeout(tabSwitchTimeout.current);
      }
    };
  }, [examState]);

  // Prevent copy/paste/cut
  useEffect(() => {
    const preventCopy = (e: ClipboardEvent) => {
      // Allow clipboard operations during submit/exit
      if (isSubmittingOrExiting.current) return;

      if (examState && !examState.isSubmitted) {
        e.preventDefault();
        // Copy is silently prevented, no violation recorded
      }
    };

    const preventPaste = (e: ClipboardEvent) => {
      // Allow clipboard operations during submit/exit
      if (isSubmittingOrExiting.current) return;

      if (examState && !examState.isSubmitted) {
        e.preventDefault();
        // Paste is silently prevented
      }
    };

    const preventCut = (e: ClipboardEvent) => {
      // Allow clipboard operations during submit/exit
      if (isSubmittingOrExiting.current) return;

      if (examState && !examState.isSubmitted) {
        e.preventDefault();
        handleViolation('cut_attempt');
      }
    };

    const preventContextMenu = (e: MouseEvent) => {
      // Allow context menu during submit/exit
      if (isSubmittingOrExiting.current) return;

      if (examState && !examState.isSubmitted) {
        e.preventDefault();
      }
    };

    const preventKeyboardShortcuts = (e: KeyboardEvent) => {
      // Allow keyboard shortcuts during submit/exit
      if (isSubmittingOrExiting.current) return;

      if (examState && !examState.isSubmitted) {
        const blockedKeys = [
          'Tab', 'Escape', 'Alt', 'Control', 'Meta', 'OS',
          'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12',
          'PrintScreen', 'ScrollLock', 'Pause'
        ];

        // Block specific keys
        if (blockedKeys.includes(e.key)) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return false;
        }

        // Block any key with Alt modifier
        if (e.altKey) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return false;
        }

        // Block any key with Ctrl modifier
        if (e.ctrlKey) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return false;
        }

        // Block any key with Meta (Windows/Cmd) modifier
        if (e.metaKey) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return false;
        }

        // Block Shift+Tab
        if (e.shiftKey && e.key === 'Tab') {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return false;
        }
      }
    };

    // Use capture phase to intercept events at the earliest possible point
    const eventOptions: AddEventListenerOptions = { capture: true, passive: false };

    // Add listeners to both document and window for maximum coverage
    document.addEventListener('copy', preventCopy);
    document.addEventListener('paste', preventPaste);
    document.addEventListener('cut', preventCut);
    document.addEventListener('contextmenu', preventContextMenu);

    // Keyboard events on document
    document.addEventListener('keydown', preventKeyboardShortcuts, eventOptions);
    document.addEventListener('keyup', preventKeyboardShortcuts, eventOptions);
    document.addEventListener('keypress', preventKeyboardShortcuts, eventOptions);

    // Keyboard events on window
    window.addEventListener('keydown', preventKeyboardShortcuts, eventOptions);
    window.addEventListener('keyup', preventKeyboardShortcuts, eventOptions);
    window.addEventListener('keypress', preventKeyboardShortcuts, eventOptions);

    return () => {
      document.removeEventListener('copy', preventCopy);
      document.removeEventListener('paste', preventPaste);
      document.removeEventListener('cut', preventCut);
      document.removeEventListener('contextmenu', preventContextMenu);

      document.removeEventListener('keydown', preventKeyboardShortcuts, eventOptions);
      document.removeEventListener('keyup', preventKeyboardShortcuts, eventOptions);
      document.removeEventListener('keypress', preventKeyboardShortcuts, eventOptions);

      window.removeEventListener('keydown', preventKeyboardShortcuts, eventOptions);
      window.removeEventListener('keyup', preventKeyboardShortcuts, eventOptions);
      window.removeEventListener('keypress', preventKeyboardShortcuts, eventOptions);
    };
  }, [examState]);

  // Auto-abandon exam due to excessive violations
  const handleAutoAbandon = useCallback(async () => {
    // Prevent multiple calls
    if (isAbandoning.current) {
      console.log('⏭️ Abandon already in progress, skipping duplicate call');
      return;
    }

    if (!attemptId) {
      console.error('Cannot auto-abandon: Exam attempt not found.');
      toast.error('Cannot abandon exam: Attempt ID not found.');
      return;
    }

    // Set flag to prevent duplicate calls
    isAbandoning.current = true;
    console.log('🔒 Setting isAbandoning flag to true');

    // STOP VIOLATION MONITORING IMMEDIATELY
    console.log('🛑 Stopping violation monitoring - exam is being auto-abandoned');
    isSubmittingOrExiting.current = true;

    try {
      console.log('🚨 Auto-abandoning exam due to excessive violations...');
      console.log('Using attempt_id:', attemptId);

      // Call the abandon attempt API (only attempt_id in URL params, no body payload)
      const response = await apiClient.post(
        API_ENDPOINTS.CERTIFICATIONS.ABANDON_ATTEMPT(attemptId),
        {} // Empty payload - attempt_id is in URL params only
      );

      console.log('Abandon API response:', response);

      if (response.data.success || response.status === 200) {
        console.log('✅ Exam auto-abandoned successfully');

        // Clear ALL saved state from localStorage for this certification
        clearAllExamStorage();

        // Unlock keyboard and exit fullscreen
        unlockKeyboard();
        if (document.fullscreenElement) {
          await document.exitFullscreen();
        }

        // Redirect to certifications page
        navigate('/learn/certifications');
      } else {
        throw new Error(response.data.message || 'Failed to auto-abandon exam');
      }
    } catch (error: any) {
      console.error('Error auto-abandoning exam:', error);

      // Clear localStorage even on error
      clearAllExamStorage();

      // Unlock keyboard and exit fullscreen
      unlockKeyboard();
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }

      // Redirect to certifications page even on error
      navigate('/learn/certifications');
    }
  }, [attemptId, navigate, examId, clearAllExamStorage]);

  // Helper function to immediately update metrics
  const updateMetricsImmediately = useCallback(async (violationCount: number) => {
    if (!attemptId || !examId) return;

    try {
      const currentTime = Date.now();
      const timeSpentThisSession = Math.floor((currentTime - examStartTime.current) / 1000);
      const totalTimeSpent = initialTimeSpentSeconds.current + timeSpentThisSession;

      console.log('🚀 Immediate metrics update triggered (violation):', {
        initialTimeSpent: initialTimeSpentSeconds.current,
        timeSpentThisSession,
        totalTimeSpent,
        violationCount
      });

      await apiClient.patch(
        API_ENDPOINTS.CERTIFICATIONS.UPDATE_METRICS(examId, attemptId),
        {
          overall_time_spent_seconds: totalTimeSpent,
          violation_count: violationCount
        }
      );

      console.log('✅ Immediate metrics update successful');
    } catch (error) {
      console.error('❌ Failed to immediately update metrics:', error);
      // Silent failure - don't interrupt the exam
    }
  }, [attemptId, examId]);

  // Handle violations with debounce
  const handleViolation = useCallback((type: string) => {
    // STOP MONITORING: If exam is being submitted/exited, don't record violations
    if (isSubmittingOrExiting.current) {
      console.log(`🚫 Violation ignored (${type}) - exam is being submitted/exited`);
      return;
    }

    if (!examState) return;

    const now = Date.now();

    // Global debounce: Only record violation if 1 second has passed since ANY violation
    const timeSinceLastGlobalViolation = now - lastGlobalViolationTime.current;
    if (timeSinceLastGlobalViolation < 1000) {
      console.log(`Global debounced violation: ${type} (${timeSinceLastGlobalViolation}ms since last)`);
      return;
    }

    const lastTime = lastViolationTime.current[type] || 0;
    const timeSinceLastViolation = now - lastTime;

    // Per-type debounce: Only record violation if 2 seconds have passed since last violation of same type
    if (timeSinceLastViolation < 2000) {
      console.log(`Debounced violation: ${type}`);
      return;
    }

    // Update last violation times
    lastViolationTime.current[type] = now;
    lastGlobalViolationTime.current = now;

    const newViolation = {
      type,
      timestamp: new Date().toISOString(),
      count: 1
    };

    // Dismiss any existing toasts to keep the UI clean during violations
    // The violation modal already provides all the necessary information
    toast.dismiss();

    setExamState(prev => {
      if (!prev) return prev;

      const newViolationCount = prev.violationCount + 1;
      const updatedViolations = [...prev.violations, newViolation];

      // Update ref for metrics
      currentViolationCount.current = newViolationCount;

      console.log(`📊 Violation Stats:
        - Previous count: ${prev.violationCount}
        - New count: ${newViolationCount}
        - Type: ${type}
        - Total violations: ${updatedViolations.length}
      `);

      // Show modal warning for every violation
      setCurrentViolationType(type);
      setShowViolationWarning(true);
      console.log('⚠️ Violation detected - showing modal warning');

      // Save violations to localStorage (certification-specific)
      saveViolationsToStorage(updatedViolations, newViolationCount);
      console.log('💾 Violations saved to localStorage');

      // Immediately update metrics with new violation count
      updateMetricsImmediately(newViolationCount);

      // Check if violation count exceeds 3 (triggers at count 4) - Auto abandon exam
      if (newViolationCount > 3) {
        console.error(`🚨 VIOLATION LIMIT EXCEEDED!`);
        console.error(`   - Current count: ${newViolationCount}`);
        console.error(`   - Threshold: > 3`);
        console.error(`   - Showing aborting popup...`);

        // Show the aborting popup
        setShowAbortingPopup(true);

        // Call abandon exam after showing popup for 2 seconds
        setTimeout(() => {
          handleAutoAbandon();
        }, 2000);
      } else {
        console.log(`✓ Violation count is ${newViolationCount}, limit not exceeded yet (needs > 3)`);
      }

      return {
        ...prev,
        violations: updatedViolations,
        violationCount: newViolationCount
      };
    });

    console.warn(`Violation detected: ${type} at ${newViolation.timestamp}`);
    console.log(`Current violation count: ${examState.violationCount + 1}`);
  }, [examState, saveViolationsToStorage, handleAutoAbandon, updateMetricsImmediately]);

  // Auto-save to localStorage whenever exam state changes
  useEffect(() => {
    if (examState && !examState.isSubmitted) {
      saveToStorage(examState);
    }
  }, [examState, saveToStorage]);

  // Timer countdown - Only start after metrics are loaded from API
  useEffect(() => {
    // Wait for metrics to be loaded from API before starting timer countdown
    // This ensures the timer shows the correct remaining time from the server
    if (!examState || examState.isSubmitted || !metricsLoaded) return;

    const timer = setInterval(() => {
      setExamState(prev => {
        if (!prev || prev.timeRemaining <= 0) {
          handleAutoSubmit();
          return prev;
        }

        // Recompute from the countdown end so tab throttling can't drift the timer
        const newTimeRemaining = countdownEndMs.current !== null
          ? Math.max(0, Math.floor((countdownEndMs.current - performance.now()) / 1000))
          : prev.timeRemaining - 1;

        // Time warnings at specific intervals
        if (newTimeRemaining === 900) { // 15 minutes
          toast('⏰ 15 minutes remaining!', {
            duration: 5000,
            id: 'time-warning-15',
            icon: '⏰'
          });
        } else if (newTimeRemaining === 600) { // 10 minutes
          toast('⏰ 10 minutes remaining!', {
            duration: 5000,
            id: 'time-warning-10',
            icon: '⏰'
          });
        } else if (newTimeRemaining === 300) { // 5 minutes
          toast.error('⏰ Only 5 minutes remaining!', {
            duration: 5000,
            id: 'time-warning-5'
          });
          if (!showTimeWarning) {
            setShowTimeWarning(true);
          }
        } else if (newTimeRemaining === 60) { // 1 minute
          toast.error('🚨 Final minute! Only 1 minute remaining!', {
            duration: 6000,
            id: 'time-warning-1'
          });
        }

        const updatedState = {
          ...prev,
          timeRemaining: newTimeRemaining
        };

        // Note: We don't save time to localStorage every second anymore
        // Only save once when continuing in-progress exam (done in loadCertificationExam)

        return updatedState;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [examState?.isSubmitted, showTimeWarning, metricsLoaded]);

  // Sync violation count ref with examState
  useEffect(() => {
    if (examState) {
      currentViolationCount.current = examState.violationCount;
    }
  }, [examState?.violationCount]);

  // Metrics update - Send time spent and violation count every 5 seconds
  useEffect(() => {
    if (!examState || examState.isSubmitted || !attemptId || !examId) {
      console.log('⏭️ Skipping metrics update interval setup:', {
        hasExamState: !!examState,
        isSubmitted: examState?.isSubmitted,
        hasAttemptId: !!attemptId,
        hasExamId: !!examId
      });
      return;
    }

    console.log('🔄 Setting up metrics update interval (every 5 seconds)');
    const timeLimitSeconds = examState.timeLimitMinutes * 60;

    const updateMetrics = async () => {
      try {
        // Calculate time spent in current session
        const currentTime = Date.now();
        const timeSpentThisSession = Math.floor((currentTime - examStartTime.current) / 1000);

        // Add initial time spent (for continuing exams)
        const totalTimeSpent = initialTimeSpentSeconds.current + timeSpentThisSession;

        console.log('📊 Updating metrics (5-second interval):', {
          initialTimeSpent: initialTimeSpentSeconds.current,
          timeSpentThisSession,
          totalTimeSpent,
          violationCount: currentViolationCount.current,
          attemptId,
          examId
        });

        const metricsResponse = await apiClient.patch(
          API_ENDPOINTS.CERTIFICATIONS.UPDATE_METRICS(examId, attemptId),
          {
            overall_time_spent_seconds: totalTimeSpent,
            violation_count: currentViolationCount.current
          }
        );

        // Re-sync with the server (after sleep or a frozen tab); ignore sub-2s network jitter
        const remaining = metricsResponse?.data?.data?.remaining_seconds;
        if (typeof remaining === 'number') {
          const syncedEndMs = getCountdownEndMs(Math.min(remaining, timeLimitSeconds));
          if (syncedEndMs !== null && (countdownEndMs.current === null || Math.abs(syncedEndMs - countdownEndMs.current) > 2000)) {
            countdownEndMs.current = syncedEndMs;
          }
        }

        console.log('✅ Metrics updated successfully at', new Date().toISOString());
      } catch (error) {
        console.error('❌ Failed to update metrics:', error);
        // Don't show error toast to avoid interrupting the exam
      }
    };

    // Call immediately on mount, then every 5 seconds
    updateMetrics();
    metricsUpdateInterval.current = setInterval(updateMetrics, 5000);

    return () => {
      console.log('🧹 Clearing metrics update interval');
      if (metricsUpdateInterval.current) {
        clearInterval(metricsUpdateInterval.current);
      }
    };
  }, [examState?.isSubmitted, attemptId, examId]);

  // Component unmount cleanup - Update metrics one final time and clear localStorage
  useEffect(() => {
    const currentExamId = examId;

    return () => {
      // Always clear localStorage when leaving the page
      if (currentExamId) {
        const stateKey = `exam_state_${currentExamId}`;
        const timerKey = `exam_timer_${currentExamId}`;
        const violationsKey = `exam_violations_${currentExamId}`;

        console.log('🧹 Component unmounting - Clearing localStorage:', {
          stateKey,
          timerKey,
          violationsKey
        });

        // Clear localStorage immediately
        localStorage.removeItem(stateKey);
        localStorage.removeItem(timerKey);
        localStorage.removeItem(violationsKey);

        // Verify removal
        const stillExists = {
          state: localStorage.getItem(stateKey),
          timer: localStorage.getItem(timerKey),
          violations: localStorage.getItem(violationsKey)
        };

        console.log('✅ localStorage cleared. Verification:', {
          stateStillExists: stillExists.state !== null,
          timerStillExists: stillExists.timer !== null,
          violationsStillExists: stillExists.violations !== null
        });

        if (stillExists.state || stillExists.timer || stillExists.violations) {
          console.error('⚠️ WARNING: Some localStorage keys still exist after cleanup!', stillExists);
        }
      }
    };
  }, [examId]);

  // Auto-save functionality - DISABLED (now manual save only via "Save Changes" button)
  // useEffect(() => {
  //   if (!examState?.autoSaveEnabled) return;

  //   autoSaveInterval.current = setInterval(() => {
  //     handleAutoSave();
  //   }, 30000); // Auto-save every 30 seconds

  //   return () => {
  //     if (autoSaveInterval.current) {
  //       clearInterval(autoSaveInterval.current);
  //     }
  //   };
  // }, [examState?.autoSaveEnabled]);

  // Prevent leaving page without confirmation (handles browser close/refresh)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (examState && !examState.isSubmitted) {
        e.preventDefault();
        e.returnValue = 'Are you sure you want to leave? Your exam progress may be lost.';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [examState?.isSubmitted]);

  // Prevent browser navigation (back/forward buttons, swipe gestures)
  useEffect(() => {
    if (!examState || examState.isSubmitted) return;

    // Push a dummy state to history when exam loads
    const handlePopState = (e: PopStateEvent) => {
      e.preventDefault();

      // Show exit confirmation modal instead of allowing navigation
      console.log('🚫 Navigation attempt detected (back/forward/swipe). Showing exit confirmation...');
      setShowExitConfirmation(true);

      // Push state back to keep user on exam page
      window.history.pushState(null, '', window.location.href);
    };

    // Add initial state to history
    window.history.pushState(null, '', window.location.href);

    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [examState, examState?.isSubmitted]);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswerChange = (questionId: string, answer: string | string[]) => {
    if (!examState) return;

    // Store answer temporarily without marking as answered
    // It will only be marked as answered when user clicks "Save Changes"
    setExamState(prev => ({
      ...prev!,
      questions: prev!.questions.map(q =>
        q._id === questionId
          ? { ...q, userAnswer: answer }
          : q
      )
    }));
  };

  // Handle checkbox toggle for multiple choice questions
  const handleMultipleChoiceToggle = (questionId: string, optionText: string) => {
    if (!examState) return;

    const question = examState.questions.find(q => q._id === questionId);
    if (!question) return;

    const currentAnswers = Array.isArray(question.userAnswer) ? question.userAnswer : [];

    let newAnswers: string[];
    if (currentAnswers.includes(optionText)) {
      // Remove if already selected
      newAnswers = currentAnswers.filter(a => a !== optionText);
      console.log('Removing option:', optionText);
    } else {
      // Add if not selected
      newAnswers = [...currentAnswers, optionText];
      console.log('Adding option:', optionText);
    }

    console.log('Multiple choice updated. Previous:', currentAnswers, 'New:', newAnswers);
    handleAnswerChange(questionId, newAnswers);
  };

  const navigateToQuestion = (questionNumber: number) => {
    if (!examState || questionNumber < 1 || questionNumber > examState.totalQuestions) return;

    // Reset question start time for time tracking
    questionStartTime.current = Date.now();

    // When navigating to a question, restore the saved answer from backend if it exists
    // This ensures unsaved local changes don't persist when navigating away and back
    setExamState(prev => {
      if (!prev) return prev;

      // Get the question we're navigating to
      const targetQuestion = prev.questions[questionNumber - 1];
      const questionId = targetQuestion._id;

      // Check if there's a saved answer in our ref
      const savedAnswer = savedAnswersRef.current.get(questionId);

      // Update the questions array to restore saved answer for the target question
      const updatedQuestions = prev.questions.map((q, index) => {
        if (index === questionNumber - 1) {
          // For the question we're navigating to, restore saved answer if exists
          if (savedAnswer) {
            console.log(`Restoring saved answer for question ${questionId}:`, savedAnswer);
            return {
              ...q,
              userAnswer: savedAnswer.answer,
              isAnswered: savedAnswer.isAnswered
            };
          } else {
            // No saved answer, clear any unsaved changes
            return {
              ...q,
              userAnswer: undefined,
              isAnswered: false
            };
          }
        }
        return q;
      });

      return {
        ...prev,
        currentQuestion: questionNumber,
        questions: updatedQuestions
      };
    });
  };

  const navigateNext = () => {
    if (examState && examState.currentQuestion < examState.totalQuestions) {
      navigateToQuestion(examState.currentQuestion + 1);
    }
  };

  const navigatePrevious = () => {
    if (examState && examState.currentQuestion > 1) {
      navigateToQuestion(examState.currentQuestion - 1);
    }
  };

  const handleAutoSave = async () => {
    if (!examState || !attemptId) {
      if (!attemptId) {
        toast.error('Exam attempt not found. Please restart the exam.');
      }
      return;
    }

    // Get current question
    const currentQ = examState.questions[examState.currentQuestion - 1];

    // Check if user has answered the current question
    if (!currentQ.userAnswer ||
        currentQ.userAnswer === '' ||
        (Array.isArray(currentQ.userAnswer) && currentQ.userAnswer.length === 0)) {
      toast.error('Please select or enter an answer before saving.');
      return;
    }

    setAutoSaveStatus('saving');

    try {
      // Calculate time spent on this question
      const timeSpentSeconds = Math.floor((Date.now() - questionStartTime.current) / 1000);

      // Prepare the answer based on question type
      let answerToSubmit: any = currentQ.userAnswer;

      // For MCQ questions, find the option ID(s)
      if (currentQ.question_type === 'SINGLE_CHOICE' || currentQ.question_type === 'TRUE_FALSE') {
        // Single choice or True/False - send single option ID
        if (currentQ.options && currentQ.options.length > 0) {
          const selectedOption = currentQ.options.find(opt => opt.text === currentQ.userAnswer);
          if (selectedOption) {
            answerToSubmit = selectedOption.id;
          }
        }
      } else if (currentQ.question_type === 'MULTIPLE_CHOICE') {
        // Multiple choice - send array of option IDs
        if (currentQ.options && currentQ.options.length > 0 && Array.isArray(currentQ.userAnswer)) {
          answerToSubmit = currentQ.userAnswer
            .map(answerText => {
              const option = currentQ.options!.find(opt => opt.text === answerText);
              return option ? option.id : null;
            })
            .filter(id => id !== null); // Remove any null values
        }
      }

      // Prepare payload
      const payload = {
        question_id: currentQ._id,
        answer: answerToSubmit,
        time_spent_seconds: timeSpentSeconds
      };

      console.log('Submitting answer for question type:', currentQ.question_type);
      console.log('User answer (raw):', currentQ.userAnswer);
      console.log('Answer to submit (processed):', answerToSubmit);
      console.log('Full payload:', payload);

      // Call the submit answer API
      const response = await apiClient.post(
        API_ENDPOINTS.CERTIFICATIONS.SUBMIT_ANSWER(attemptId),
        payload
      );

      if (response.data.success || response.status === 200) {
        setAutoSaveStatus('saved');
        toast.success('Answer saved successfully!');

        // Immediately mark the current question as answered
        setExamState(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            questions: prev.questions.map(q =>
              q._id === currentQ._id
                ? { ...q, isAnswered: true }
                : q
            )
          };
        });

        // Reset question start time for next save
        questionStartTime.current = Date.now();

        // Fetch latest attempt details from API to update the UI with saved answers
        console.log('💾 Answer saved! Refreshing exam state from API...');
        await fetchLatestAttemptDetails();

        console.log('✅ Answer saved and exam state refreshed from API');

        // Automatically navigate to next question if not on last question
        if (examState.currentQuestion < examState.totalQuestions) {
          setTimeout(() => {
            navigateNext();
          }, 500); // Small delay to let user see the success message
        }
      } else {
        throw new Error(response.data.message || 'Failed to save answer');
      }
    } catch (error: any) {
      console.error('Error saving answer:', error);
      setAutoSaveStatus('error');

      const errorData = error?.response?.data;
      const errorMessage = errorData?.error?.error_description ||
                          errorData?.error?.message ||
                          errorData?.message ||
                          error?.message ||
                          'Failed to save answer. Please try again.';

      toast.error(errorMessage);

      // Server says time is over: finish the attempt instead of leaving it open
      if (errorData?.error?.error === 'TIME_LIMIT_EXCEEDED') {
        handleAutoSubmit();
      }
    }
  };

  const handleClearAnswer = async () => {
    if (!examState || !attemptId) {
      if (!attemptId) {
        toast.error('Exam attempt not found. Please restart the exam.');
      }
      return;
    }

    // Get current question
    const currentQ = examState.questions[examState.currentQuestion - 1];

    // Check if the question has a saved answer
    if (!currentQ.isAnswered) {
      toast.error('No saved answer to clear for this question.');
      return;
    }

    try {
      console.log('🗑️ Clearing answer for question:', currentQ._id);

      // Show loading toast
      const loadingToast = toast.loading('Clearing answer...');

      // Call the delete answer API
      const response = await apiClient.delete(
        API_ENDPOINTS.CERTIFICATIONS.DELETE_ANSWER(attemptId, currentQ._id)
      );

      // Dismiss loading toast
      toast.dismiss(loadingToast);

      if (response.data.success || response.status === 200) {
        toast.success('Answer cleared successfully!');

        // Remove from savedAnswersRef
        savedAnswersRef.current.delete(currentQ._id);

        // Update the question state to clear the answer
        setExamState(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            questions: prev.questions.map(q =>
              q._id === currentQ._id
                ? { ...q, userAnswer: undefined, isAnswered: false }
                : q
            )
          };
        });

        // Fetch latest attempt details from API to ensure sync
        console.log('🔄 Refreshing exam state from API...');
        await fetchLatestAttemptDetails();

        console.log('✅ Answer cleared and exam state refreshed from API');
      } else {
        throw new Error(response.data.message || 'Failed to clear answer');
      }
    } catch (error: any) {
      console.error('Error clearing answer:', error);

      const errorData = error?.response?.data;
      const errorMessage = errorData?.error?.error_description ||
                          errorData?.error?.message ||
                          errorData?.message ||
                          error?.message ||
                          'Failed to clear answer. Please try again.';

      toast.error(errorMessage);
    }
  };

  const handleSubmitExam = async () => {
    console.log('🔵 handleSubmitExam called');
    console.log('   - examState exists:', !!examState);
    console.log('   - attemptId:', attemptId);

    if (!examState || !attemptId) {
      console.error('❌ Cannot submit exam: Missing examState or attemptId');
      if (!attemptId) {
        toast.error('Exam attempt not found. Cannot submit exam.');
      }
      if (!examState) {
        toast.error('Exam state not found. Please refresh and try again.');
      }
      setShowSubmitConfirmation(false);
      return;
    }

    // STOP VIOLATION MONITORING IMMEDIATELY
    console.log('🛑 Stopping violation monitoring - exam is being submitted');
    isSubmittingOrExiting.current = true;

    // STOP METRICS UPDATE IMMEDIATELY
    console.log('🛑 Stopping metrics update interval - exam is being submitted');
    if (metricsUpdateInterval.current) {
      clearInterval(metricsUpdateInterval.current);
      metricsUpdateInterval.current = undefined;
    }

    // Set submitting state to show loader
    setIsSubmitting(true);

    try {
      // Log answer stats (but allow submission regardless)
      const answeredCount = examState.questions.filter(q => q.isAnswered).length;
      const unansweredCount = examState.questions.filter(q => !q.isAnswered).length;
      console.log('📊 Answer check:');
      console.log('   - Total questions:', examState.questions.length);
      console.log('   - Answered:', answeredCount);
      console.log('   - Unanswered:', unansweredCount);

      if (unansweredCount > 0) {
        console.warn(`⚠️ Submitting exam with ${unansweredCount} unanswered question(s)`);
      }

      console.log('📤 Submitting exam with attempt_id:', attemptId);
      console.log('   - API endpoint:', API_ENDPOINTS.CERTIFICATIONS.SUBMIT_EXAM(attemptId));

      // Close the modal and show loading state
      setShowSubmitConfirmation(false);
      toast.loading('Submitting exam...', { id: 'submit-exam' });

      // Call the submit exam API (only attempt_id in URL params, no body payload)
      const response = await apiClient.post(
        API_ENDPOINTS.CERTIFICATIONS.SUBMIT_EXAM(attemptId),
        {} // Empty payload - attempt_id is in URL params only
      );

      console.log('✅ API Response received:', response);
      console.log('   - Status:', response.status);
      console.log('   - Data:', response.data);

      // Dismiss loading toast
      toast.dismiss('submit-exam');

      if (response.data.success || response.status === 200) {
        console.log('✅ Exam submitted successfully');

        // Clear ALL saved state from localStorage for this certification
        console.log('🧹 Clearing localStorage after successful submission...');
        clearAllExamStorage();

        // Unlock keyboard and exit fullscreen
        unlockKeyboard();
        if (document.fullscreenElement) {
          await document.exitFullscreen();
        }

        // Prepare success page data
        const answeredCount = examState.questions.filter(q => q.isAnswered).length;
        let earnedBadges: EarnedBadge[] = [];

        // Fetch gamification metrics after successful submission
        if (certificationData?.certification_id) {
          try {
            console.log('🎮 Fetching gamification metrics for certification:', certificationData.certification_id);
            const metricsData = await Leaderboardservice.getMyCertificationMetrics(certificationData.certification_id);

            console.log('✅ Gamification metrics received:', metricsData);

            // Check for new badges
            if (metricsData.new_badges && metricsData.new_badges.length > 0) {
              console.log('🏆 New badges earned:', metricsData.new_badges.map(b => b.badge_name));
              earnedBadges = metricsData.new_badges;

              // Acknowledge badges immediately
              try {
                const badgeIds = metricsData.new_badges.map(b => b.badge_id);
                console.log('🏆 [Badges] Acknowledging badges:', badgeIds);

                await Leaderboardservice.acknowledgeBadges({
                  badge_ids: badgeIds,
                  source_type: 'CERTIFICATION',
                  source_id: certificationData.certification_id
                });

                console.log('🏆 [Badges] Badges acknowledged successfully');
              } catch (ackError) {
                console.error('🏆 [Badges] Failed to acknowledge badges:', ackError);
              }
            }
          } catch (metricsError) {
            console.error('❌ Failed to fetch gamification metrics:', metricsError);
          }
        }

        // Navigate to success page
        navigate(`/learn/exams/success/${examId}`, {
          replace: true,
          state: {
            examTitle: examState.title,
            totalQuestions: examState.totalQuestions,
            answeredCount,
            violationCount: examState.violationCount,
            submittedAt: new Date().toLocaleString(),
            pendingBadges: earnedBadges
          }
        });
      } else {
        // Reset loading state on error
        setIsSubmitting(false);
        throw new Error(response.data.message || 'Failed to submit exam');
      }
    } catch (error: any) {
      console.error('❌ Failed to submit exam:', error);
      console.error('   - Error response:', error?.response);
      console.error('   - Error data:', error?.response?.data);

      // Dismiss loading toast if it exists
      toast.dismiss('submit-exam');

      // Reset loading state
      setIsSubmitting(false);

      const errorData = error?.response?.data;

      // A duplicate submit (e.g. auto-submit racing a manual one) means the attempt is already finished
      if (errorData?.error?.error === 'ATTEMPT_ALREADY_SUBMITTED') {
        clearAllExamStorage();
        unlockKeyboard();
        if (document.fullscreenElement) {
          await document.exitFullscreen();
        }
        toast.success('This exam has already been submitted.');
        navigate('/learn/certifications', { replace: true });
        return;
      }

      const errorMessage = errorData?.error?.error_description || errorData?.message || error?.message || 'Failed to submit exam. Please try again.';
      toast.error(errorMessage);

      // Don't close the modal on error - let user try again
      // setShowSubmitConfirmation(false); // Removed this line
    }
  };

  // Keep the latest submit handler for the timer, whose interval closure can be stale
  submitExamRef.current = handleSubmitExam;

  const handleAutoSubmit = () => {
    if (autoSubmitTriggered.current) return;
    autoSubmitTriggered.current = true;
    toast.error('Time is up! Submitting exam automatically...', { id: 'time-up' });
    submitExamRef.current();
  };

  const handleExitExam = async () => {
    // Prevent multiple calls
    if (isExiting) {
      console.log('⏭️ Exit already in progress, skipping duplicate call');
      return;
    }

    if (!attemptId) {
      console.error('Cannot exit: Exam attempt not found.');
      toast.error('Exam attempt not found.');
      return;
    }

    // STOP VIOLATION MONITORING IMMEDIATELY
    console.log('🛑 Stopping violation monitoring - exam is being exited');
    isSubmittingOrExiting.current = true;

    // STOP METRICS UPDATE IMMEDIATELY
    console.log('🛑 Stopping metrics update interval - exam is being exited');
    if (metricsUpdateInterval.current) {
      clearInterval(metricsUpdateInterval.current);
      metricsUpdateInterval.current = undefined;
    }

    try {
      setIsExiting(true);
      console.log('🚪 User clicked EXIT. Calling abandon API with attempt_id:', attemptId);

      // Call the abandon attempt API (only attempt_id in URL params, no body payload)
      const response = await apiClient.post(
        API_ENDPOINTS.CERTIFICATIONS.ABANDON_ATTEMPT(attemptId),
        {} // Empty payload - attempt_id is in URL params only
      );

      console.log('Exit API response:', response);

      if (response.data.success || response.status === 200) {
        console.log('✅ Exam abandoned successfully via EXIT button');
        toast.success('Exam abandoned successfully.');

        // Clear ALL saved state from localStorage for this certification
        clearAllExamStorage();

        // Unlock keyboard and exit fullscreen
        unlockKeyboard();
        if (document.fullscreenElement) {
          await document.exitFullscreen();
        }

        // Navigate back to certifications page immediately
        navigate('/learn/certifications');
      } else {
        throw new Error(response.data.message || 'Failed to abandon exam');
      }
    } catch (error: any) {
      console.error('❌ Error abandoning exam:', error);
      console.error('Error details:', error.response?.data);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to exit exam. Please try again.';
      toast.error(errorMessage);

      // Clear localStorage even on error
      clearAllExamStorage();

      setIsExiting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading exam...</p>
        </div>
      </div>
    );
  }

  if (!examState || !certificationData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">Exam not found</p>
          <button
            onClick={() => navigate('/learn/certifications')}
            className="px-4 py-2 bg-brand-purple-600 text-white rounded-lg hover:bg-brand-purple-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = examState.questions[examState.currentQuestion - 1];
  const answeredCount = examState.questions.filter(q => q.isAnswered).length;

  return (
    <div
      ref={examContainerRef}
      className="min-h-screen bg-gray-50 flex flex-col"
      onClick={handleExamContainerClick}
    >
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-2 md:px-6 py-2 md:py-4 flex-shrink-0">
        <div className="flex items-center justify-between gap-2">
          {/* Left Section - Title and Question */}
          <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-1">
            <Shield className="w-4 h-4 md:w-6 md:h-6 text-purple-600 flex-shrink-0" />
            <h1 className="text-sm md:text-xl font-semibold text-gray-900 truncate">{examState.title}</h1>
            <div className="flex items-center gap-1 md:gap-2 text-xs md:text-sm text-gray-600 flex-shrink-0">
              <FileText className="w-3 h-3 md:w-4 md:h-4" />
              <span className="whitespace-nowrap">Q {examState.currentQuestion}/{examState.totalQuestions}</span>
            </div>
          </div>

          {/* Right Section - EXIT, Violations, Save Status, and Timer */}
          <div className="flex items-center gap-1 md:gap-3 flex-shrink-0">
            {/* EXIT Button */}
            <button
              onClick={() => setShowExitConfirmation(true)}
              className="flex items-center gap-1 px-2 md:px-3 py-1 md:py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-xs md:text-sm font-medium whitespace-nowrap transition-colors"
              title="Exit Exam"
            >
              <LogOut className="w-3 h-3 md:w-4 md:h-4" />
              <span className="hidden sm:inline">Exit</span>
            </button>

            {/* Violation Indicator */}
            {examState.violationCount > 0 && (
              <div className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-lg text-xs md:text-sm font-medium whitespace-nowrap">
                <AlertTriangle className="w-3 h-3 md:w-4 md:h-4" />
                <span className="hidden sm:inline">{examState.violationCount} Violation{examState.violationCount !== 1 ? 's' : ''}</span>
                <span className="sm:hidden">{examState.violationCount}</span>
              </div>
            )}

            {/* Auto-save Status */}
            <div className="flex items-center text-xs md:text-sm">
              {autoSaveStatus === 'saving' && (
                <div className="flex items-center gap-1 text-blue-600">
                  <RefreshCw className="w-3 h-3 md:w-4 md:h-4 animate-spin" />
                  <span className="hidden md:inline">Saving...</span>
                </div>
              )}
            </div>

            {/* Timer */}
            <div className={`flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1 md:py-2 rounded-lg font-mono font-bold text-xs md:text-base whitespace-nowrap ${
              !metricsLoaded ? 'bg-gray-100 text-gray-500' :
              examState.timeRemaining < 300 ? 'bg-red-100 text-red-700' :
              examState.timeRemaining < 900 ? 'bg-yellow-100 text-yellow-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              <Timer className={`w-3 h-3 md:w-5 md:h-5 ${!metricsLoaded ? 'animate-pulse' : ''}`} />
              {metricsLoaded ? formatTime(examState.timeRemaining) : 'Loading...'}
            </div>

            {/* Toggle Navigation */}
            <button
              onClick={() => setShowNavigationPanel(!showNavigationPanel)}
              className="p-1.5 md:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg flex-shrink-0"
              title="Toggle Navigation Panel"
            >
              {showNavigationPanel ? <Eye className="w-4 h-4 md:w-5 md:h-5" /> : <EyeOff className="w-4 h-4 md:w-5 md:h-5" />}
            </button>
          </div>
        </div>

        {/* Security Notice */}
        <div className="mt-2 md:mt-3 flex items-start gap-2 text-xs text-gray-500">
          <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
          <span className="hidden sm:inline">This exam is monitored. Switching tabs, copying content, or exiting fullscreen will be recorded as violations.</span>
          <span className="sm:hidden">Monitored exam. Tab switching & violations tracked.</span>
        </div>
        
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Navigation Panel */}
        {showNavigationPanel && (
          <div className="hidden lg:flex w-80 bg-white border-r border-gray-200 flex-col">
            {/* Fixed Header */}
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Question Overview</h3>
            </div>

            {/* Scrollable Question Grid */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="grid grid-cols-5 gap-2 mb-4">
                {examState.questions.map((question, index) => (
                  <button
                    key={question._id}
                    onClick={() => navigateToQuestion(index + 1)}
                    className={`w-10 h-10 rounded-lg text-sm font-medium border-2 relative ${
                      examState.currentQuestion === index + 1
                        ? 'border-brand-purple-600 bg-brand-purple-600 text-white'
                        : question.isAnswered
                        ? 'border-green-600 bg-green-50 text-green-700'
                        : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Answered:</span>
                  <span className="font-medium">{answeredCount}/{examState.totalQuestions}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Unanswered:</span>
                  <span className="font-medium text-red-600">{examState.totalQuestions - answeredCount}</span>
                </div>
              </div>
            </div>

            <div className="p-4 border-b border-gray-200">
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900">Legend</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-purple-600 bg-purple-600 rounded"></div>
                    <span>Current Question</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-green-600 bg-green-50 rounded"></div>
                    <span>Answered</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-gray-300 bg-white rounded"></div>
                    <span>Not Answered</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-200">
              <button
                onClick={() => setShowSubmitConfirmation(true)}
                className="w-full px-4 py-3 bg-brand-green-600 text-white rounded-lg hover:bg-brand-green-700 font-medium flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4" />
                Submit Exam
              </button>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto">
            <div className="max-w-4xl mx-auto">
              {/* Question Header */}
              <div className="flex items-start justify-between mb-4 md:mb-6">
                <div className="flex-1">
                  <div className="flex items-center gap-2 md:gap-4 mb-4 flex-wrap">
                    <h2 className="text-base md:text-lg font-semibold text-gray-900">
                      Question {examState.currentQuestion}
                    </h2>
                    <span className="px-2 md:px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs md:text-sm font-medium">
                      {currentQuestion.points} {currentQuestion.points === 1 ? 'pt' : 'pts'}
                    </span>
                    <span className="px-2 md:px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs md:text-sm capitalize">
                      {currentQuestion.question_type.replace('_', ' ')}
                    </span>
                    {/* Show options count only for MCQ and True/False, not text */}
                    {((currentQuestion.question_type === 'multiple_choice' || currentQuestion.question_type === 'mcq') &&
                      currentQuestion.options && currentQuestion.options.length > 0) && (
                      <span className="px-2 md:px-3 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs md:text-sm font-medium">
                        {currentQuestion.options.length} options
                      </span>
                    )}
                    {currentQuestion.question_type === 'true_false' && (
                      <span className="px-2 md:px-3 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs md:text-sm font-medium">
                        2 options
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Question Content */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 md:p-8 mb-4 sm:mb-6 shadow-sm">
                <div className="prose prose-base md:prose-lg max-w-none mb-5 md:mb-8">
                  <p className="text-gray-900 leading-relaxed text-base md:text-lg select-text">{currentQuestion.question_text}</p>
                </div>

                {/* Answer Options */}
                <div className="space-y-4">
                  {/* Check question type - if TEXT_ANSWER show textarea, otherwise show options */}
                  {currentQuestion.question_type === 'TEXT_ANSWER' ? (
                    /* Text Answer Questions - Show textarea ONLY */
                    <div>
                      {/* <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm text-blue-900 font-medium mb-1">Text Answer Required</p>
                            <p className="text-sm text-blue-800">
                              Please type your answer in the text box below. Be clear, specific, and provide detailed explanations to demonstrate your understanding.
                            </p>
                          </div>
                        </div>
                      </div> */}
                      <textarea
                        value={currentQuestion.userAnswer as string || ''}
                        onChange={(e) => handleAnswerChange(currentQuestion._id, e.target.value)}
                        placeholder="Type your answer here..."
                        className="w-full h-48 p-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-purple-500 focus:border-transparent resize-y select-text"
                        style={{ userSelect: 'text' }}
                      />
                      <div className="flex justify-between gap-3 mt-2 text-xs sm:text-sm text-gray-500">
                        <span>Tip: Review your answer before moving to the next question.</span>
                        <span className="font-medium whitespace-nowrap">{(currentQuestion.userAnswer as string || '').length} characters</span>
                      </div>
                    </div>
                  ) : currentQuestion.options && currentQuestion.options.length > 0 ? (
                    /* Show Options for MCQ and other types with options */
                    <div className="space-y-3">
                      {currentQuestion.question_type === 'MULTIPLE_CHOICE' ? (
                        /* Multiple Choice - Checkboxes */
                        <>
                          {/* <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-sm text-blue-800">
                              <strong>Multiple Choice:</strong> You can select multiple answers
                              {Array.isArray(currentQuestion.userAnswer) && currentQuestion.userAnswer.length > 0 && (
                                <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                                  {currentQuestion.userAnswer.length} selected
                                </span>
                              )}
                            </p>
                          </div> */}
                          {currentQuestion.options.map((option, index) => {
                            const isChecked = Array.isArray(currentQuestion.userAnswer)
                              ? currentQuestion.userAnswer.includes(option.text)
                              : false;

                            return (
                              <label
                                key={option.id || index}
                                className={`flex items-start gap-3 md:gap-4 p-3.5 md:p-5 border-2 rounded-xl hover:bg-gray-50 cursor-pointer transition-all ${
                                  isChecked
                                    ? 'border-brand-purple-600 bg-brand-purple-50'
                                    : 'border-gray-200'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => handleMultipleChoiceToggle(currentQuestion._id, option.text)}
                                  className="mt-1 w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-brand-purple-500"
                                />
                                <span className="flex-1 text-gray-700 text-base select-text">{option.text}</span>
                              </label>
                            );
                          })}
                        </>
                      ) : (
                        /* Single Choice - Radio Buttons */
                        currentQuestion.options.map((option, index) => (
                          <label
                            key={option.id || index}
                            className={`flex items-start gap-3 md:gap-4 p-3.5 md:p-5 border-2 rounded-xl hover:bg-gray-50 cursor-pointer transition-all ${
                              currentQuestion.userAnswer === option.text
                                ? 'border-brand-purple-600 bg-brand-purple-50'
                                : 'border-gray-200'
                            }`}
                          >
                            <input
                              type="radio"
                              name={`question-${currentQuestion._id}`}
                              value={option.text}
                              checked={currentQuestion.userAnswer === option.text}
                              onChange={(e) => handleAnswerChange(currentQuestion._id, e.target.value)}
                              className="mt-1 w-5 h-5 text-purple-600 border-gray-300 focus:ring-brand-purple-500"
                            />
                            <span className="flex-1 text-gray-700 text-base select-text">{option.text}</span>
                          </label>
                        ))
                      )}
                    </div>
                  ) : (
                    /* No options available - show text answer as fallback */
                    <div>
                      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm text-blue-900 font-medium mb-1">Text Answer Required</p>
                            <p className="text-sm text-blue-800">
                              Please type your answer in the text box below. Be clear, specific, and provide detailed explanations to demonstrate your understanding.
                            </p>
                          </div>
                        </div>
                      </div>
                      <textarea
                        value={currentQuestion.userAnswer as string || ''}
                        onChange={(e) => handleAnswerChange(currentQuestion._id, e.target.value)}
                        placeholder="Type your answer here..."
                        className="w-full h-48 p-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-purple-500 focus:border-transparent resize-y select-text"
                        style={{ userSelect: 'text' }}
                      />
                      <div className="flex justify-between gap-3 mt-2 text-xs sm:text-sm text-gray-500">
                        <span>Tip: Review your answer before moving to the next question.</span>
                        <span className="font-medium whitespace-nowrap">{(currentQuestion.userAnswer as string || '').length} characters</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Navigation */}
              {/* Mobile: a 2x2 grid — Save | Clear on top, Previous | Next below,
                  so the two navigation buttons sit side by side where the thumb
                  is. The inner group is `contents` on mobile so its buttons join
                  the outer wrap and `order-*` can place them; from sm up it is a
                  normal flex group and the original one-row layout returns. */}
              <div className="flex flex-wrap sm:flex-nowrap items-stretch sm:items-center justify-between gap-2 sm:gap-3">
                <button
                  onClick={navigatePrevious}
                  disabled={examState.currentQuestion === 1}
                  className="order-3 sm:order-none basis-[calc(50%-0.25rem)] sm:basis-auto flex items-center justify-center gap-2 px-4 md:px-6 py-2.5 md:py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm md:text-base"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Previous</span>
                </button>

                <div className="contents sm:flex sm:items-center sm:gap-2 md:gap-3">
                  <button
                    onClick={() => handleAutoSave()}
                    disabled={autoSaveStatus === 'saving'}
                    className={`order-1 sm:order-none basis-[calc(50%-0.25rem)] sm:basis-auto flex items-center justify-center gap-2 px-3 md:px-5 py-2.5 md:py-3 rounded-lg font-medium text-sm md:text-base ${
                      autoSaveStatus === 'saving'
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    }`}
                  >
                    <Save className="w-4 h-4" />
                    <span className="hidden sm:inline">
                      {autoSaveStatus === 'saving' ? 'Saving...' : 'Save Response'}
                    </span>
                    <span className="sm:hidden">
                      {autoSaveStatus === 'saving' ? 'Saving...' : 'Save'}
                    </span>
                  </button>

                  {/* Clear Button - Only enabled if question is answered (saved) */}
                  <button
                    onClick={() => handleClearAnswer()}
                    disabled={!currentQuestion.isAnswered}
                    className={`order-2 sm:order-none basis-[calc(50%-0.25rem)] sm:basis-auto flex items-center justify-center gap-2 px-3 md:px-5 py-2.5 md:py-3 rounded-lg font-medium text-sm md:text-base ${
                      currentQuestion.isAnswered
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                    title={currentQuestion.isAnswered ? 'Clear saved Response' : 'No saved answer to clear'}
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="hidden sm:inline">Clear Response</span>
                    <span className="sm:hidden">Clear</span>
                  </button>

                  {examState.currentQuestion === examState.totalQuestions ? (
                    <button
                      onClick={() => setShowSubmitConfirmation(true)}
                      className="order-4 sm:order-none basis-[calc(50%-0.25rem)] sm:basis-auto whitespace-nowrap flex items-center justify-center gap-2 px-4 md:px-6 py-2.5 md:py-3 bg-brand-green-600 text-white rounded-lg hover:bg-brand-green-700 font-medium shadow-lg text-sm md:text-base"
                    >
                      <Send className="w-4 h-4" />
                      <span>Submit Exam</span>
                    </button>
                  ) : (
                    <button
                      onClick={navigateNext}
                      className="order-4 sm:order-none basis-[calc(50%-0.25rem)] sm:basis-auto flex items-center justify-center gap-2 px-4 md:px-6 py-2.5 md:py-3 bg-brand-purple-600 text-white rounded-lg hover:bg-brand-purple-700 font-medium text-sm md:text-base"
                    >
                      <span>Next</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Violation Warning Modal */}
      {showViolationWarning && examState && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          {/* Outer box clips to the rounded corners; the inner box scrolls, so the scrollbar stays inside them. */}
          <div className="bg-white rounded-xl max-w-md w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex-1 min-h-0 p-6 overflow-y-auto">
            <div className="text-center">
              <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {currentViolationType === 'page_refresh' ? 'Exam Resumed' : 'Violation Detected'}
              </h3>
              <p className="text-gray-600 mb-4">
                {currentViolationType === 'page_refresh'
                  ? 'Your exam has been resumed after page refresh.'
                  : <>A potential violation has been detected: <strong>{currentViolationType.replace(/_/g, ' ')}</strong></>
                }
              </p>
              {/* Show current violation count */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-amber-800 font-semibold">
                  Total Violations: {examState.violationCount} / 4
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  Exam will be abandoned after 4 violations
                </p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-red-800">
                  <strong>Warning:</strong> Continued violations may result in:
                </p>
                <ul className="text-sm text-red-700 mt-2 text-left list-disc list-inside">
                  <li>Exam being flagged for review</li>
                  <li>Potential disqualification</li>
                  <li>Academic misconduct proceedings</li>
                </ul>
              </div>
              <button
                onClick={() => {
                  setShowViolationWarning(false);
                  // Try to re-enter fullscreen when user closes the modal
                  enterFullscreen();
                }}
                className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
              >
                I Understand
              </button>
            </div>
          </div>
          </div>
        </div>
      )}

      {/* Time Warning Modal */}
      {showTimeWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="text-center">
              <Clock className="w-12 h-12 text-orange-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Time Warning</h3>
              <p className="text-gray-600 mb-6">
                You have less than 5 minutes remaining to complete your exam.
              </p>
              <button
                onClick={() => setShowTimeWarning(false)}
                className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submit Confirmation Modal */}
      {showSubmitConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="text-center">
              <Send className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Submit Exam?</h3>
              <p className="text-gray-600 mb-4">
                Are you sure you want to submit your exam? This action cannot be undone.
              </p>

              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Total Questions:</span>
                    <span className="font-medium">{examState.totalQuestions}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Answered:</span>
                    <span className="font-medium text-green-600">{answeredCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Unanswered:</span>
                    <span className="font-medium text-red-600">{examState.totalQuestions - answeredCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Time Remaining:</span>
                    <span className="font-medium">{metricsLoaded ? formatTime(examState.timeRemaining) : 'Loading...'}</span>
                  </div>
                  {examState.violationCount > 0 && (
                    <div className="flex justify-between pt-2 border-t border-gray-200">
                      <span>Violations:</span>
                      <span className="font-medium text-red-600">{examState.violationCount}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowSubmitConfirmation(false)}
                  disabled={isSubmitting}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium ${
                    isSubmitting
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Continue Exam
                </button>
                <button
                  onClick={handleSubmitExam}
                  disabled={isSubmitting}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2 ${
                    isSubmitting
                      ? 'bg-brand-green-400 text-white cursor-not-allowed'
                      : 'bg-brand-green-600 text-white hover:bg-brand-green-700'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Now'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Submitting Overlay Loader */}
      {isSubmitting && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl p-8 max-w-sm w-full mx-4 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <div className="w-10 h-10 border-4 border-brand-green-600 border-t-transparent rounded-full animate-spin" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Submitting Your Exam</h3>
            <p className="text-gray-600 mb-4">
              Please wait while we process your submission. This may take a few moments...
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-900">
                <strong>Important:</strong> Do not close or refresh this page.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Exit Confirmation Modal */}
      {showExitConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex-1 min-h-0 p-6 overflow-y-auto">
            <div className="text-center">
              <LogOut className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Exit Exam?</h3>
              <p className="text-gray-600 mb-4">
                If you exit, your attempt will be marked as abandoned and you cannot resume from where you left off.
              </p>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-amber-900 font-semibold mb-2">
                  What happens when you exit:
                </p>
                <ul className="text-sm text-amber-800 text-left list-disc list-inside space-y-1.5">
                  <li>This attempt will be marked as <strong>Abandoned</strong></li>
                  <li>Your saved answers will be kept for review</li>
                  <li>You <strong>cannot resume</strong> from this point</li>
                  <li>{certificationData?.allow_retry !== false
                      ? 'You can retake this exam as a new attempt if retries are available'
                      : 'No retries allowed - this will be your final attempt'}</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowExitConfirmation(false)}
                  disabled={isExiting}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue Exam
                </button>
                <button
                  onClick={handleExitExam}
                  disabled={isExiting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isExiting ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Exiting...
                    </>
                  ) : (
                    'Exit Exam'
                  )}
                </button>
              </div>
            </div>
          </div>
          </div>
        </div>
      )}

      {/* Exam Aborting Popup - shown when violation limit exceeded */}
      {showAbortingPopup && (
        <div className="fixed inset-0 bg-white bg-opacity-100 flex items-center justify-center z-50">
          <div className="bg-gray-100 rounded-xl p-8 max-w-md w-full mx-4 text-center">
            <AlertTriangle className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-red-600 mb-3">Exam Aborting</h3>
            <p className="text-gray-700 mb-4">
              Too many violations detected. Your exam is being terminated.
            </p>
            <div className="flex items-center justify-center gap-2 text-red-500">
              <RefreshCw className="w-5 h-5 animate-spin" />
              <span>Redirecting...</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
